import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { AUTH_TOKEN_KEY } from "../../../constants/index.ts";
import { useTenantSetting } from "../../../hooks/use-tenant-setting.ts";
import { writeTenant } from "../../../logic/tenant-storage.ts";

/**
 * This hook performs the following login tasks, given an tenant:
 *
 * 1. Checks for an existing tenant in local storage.
 * 2. If there's an existing tenant and it's different from the current tenant, then
 *    clear the API token and invalidate all queries.
 * 3. Sets the tenant inside local storage for use in the queries in `api/`
 *
 * The write goes through `writeTenant`, so that the in-tab mirror stays
 * current. `setTenantHeader` depends on this.
 */
export function useSetTenant(tenant: string | null) {
  const initialTenant = useTenantSetting();

  const queryClient = useQueryClient();

  const originalTenantRef = useRef(initialTenant);

  useEffect(() => {
    if (
      originalTenantRef.current &&
      tenant &&
      originalTenantRef.current !== tenant
    ) {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      queryClient
        .invalidateQueries({ queryKey: [] })
        .catch((error) => console.error("Failed to invalidate queries", error));
    }

    if (tenant) {
      writeTenant(tenant);
    }
  }, [tenant, queryClient]);
}
